import React from "react";
import { EnforcementIntentDetail } from "common/graphql";
import Skeleton from "@mui/material/Skeleton";
import Alert from "@mui/material/Alert";
import type { Theme } from "@mui/material/styles";
import NoticeStatusStepper from "./NoticeStatusStepper";

export const NoticeStatusHeader: React.FC<NoticeStatusHeaderProps> = ({ loading, intent }) => {
  if (loading || !intent) {
    return <Skeleton variant="rectangular" width="100%" height="50px" />;
  }

  if (intent.status === "voided") {
    const officerName = intent.officerName;
    // No `status === "denied"` filter. A system void records a reason while
    // leaving the officer review at 'pending' (the plate-correction auto-void,
    // AUT-5543) or 'expired' (bulk voids) — so requiring 'denied' hid the reason
    // on every such notice, including all 400 backfilled under AUT-5511, none of
    // which is 'denied'. AUT-5551.
    //
    // Matching on deniedReason (not just type) also sidesteps AUT-5558: reviews has no
    // order_by in enforcementIntentSummaryFragment, so an intent with more than one
    // officer review could otherwise land on one whose deniedReason is null.
    const deniedReason = intent.reviews.find((r) => r.type === "officer" && r.deniedReason)?.deniedReason;

    let voidText = "This notice has been voided.";
    if (deniedReason) {
      // The reason is interpolated raw, exactly as the officer sentence has always
      // done, so both banner variants read consistently. Note this is lowercase as
      // stored, and so differs from the notices grid, which title-cases the same
      // field in its valueFormatter — consistency within the banner was preferred
      // over matching the grid.
      //
      // Attribution only when we know who decided. A system void has no officer,
      // so it gets the same sentence without the "by <name>" clause.
      voidText = officerName
        ? `This notice has been voided by ${officerName} for the following reason: "${deniedReason}"`
        : `This notice has been voided for the following reason: "${deniedReason}"`;
    }

    return (
      <Alert
        severity="info"
        sx={(theme: Theme) => ({ color: theme.palette.text.secondary, backgroundColor: theme.palette.grey[300] })}
      >
        {voidText}
      </Alert>
    );
  }

  if (intent.roLookupSubmittedAt && !intent.initialLetterSentAt) {
    return <Alert severity="info">This notice has been submitted for registered owner lookup.</Alert>;
  }

  if (intent.roLookupSubmittedAt && intent.initialLetterSentAt) {
    return <NoticeStatusStepper intent={intent} />;
  }

  return null;
};

export interface NoticeStatusHeaderProps {
  loading?: boolean;
  intent?: EnforcementIntentDetail;
}

export default NoticeStatusHeader;
